import type { NaturalLanguageAtomState, NaturalLanguageDisplayMode } from '../../types/naturalLanguageAtomState';
import { NLP_STATE_CHANGE_EVENT } from './interface';
import type { NLPEditorService, NLPEditorServiceInitOptions, NLPStateChangeEvent } from './interface';
import {
    handleAcceptSuggestion,
    handleCodeChange,
    handleConfirmParsedResponse,
    handleEditParsedResponse,
    handleEditParsedResponseError,
    handleNaturalLanguageChange,
    handleParsedResponse,
    handleParsedResponseError,
    handleParsing,
    handleRejectParsedResponse,
    handleSetMode,
} from './naturalLanguageAtomState';
import type { CodeEditorResult } from 'src/features/code-editor';
import { inject, ReactiveInjectable, reacts } from 'src/features/ioc';
import { enableLlmNlp } from 'src/lib/featureFlags/flags/enableLlmNlp';
import { parseNaturalLanguageP } from 'src/lib/nlp';
import { NlpQueryType } from 'src/lib/nlp/contracts';
import type { NlpSuggestion } from 'src/lib/nlp/contracts';
import { backtestAnalyzer } from 'src/lib/parser';
import type { ImportsManagerModel } from 'src/models/ImportsManagerModel';
import type { ReactBindings } from 'src/types/bindings';
import { v4 } from 'uuid';

export class NLPEditorServiceImpl extends ReactiveInjectable implements NLPEditorService {
    #importsManager!: ImportsManagerModel;

    #nlpState!: NaturalLanguageAtomState;

    #id = v4();

    #isSubmitting = false;

    #isConfirming = false;

    #queryType: NlpQueryType = NlpQueryType.condition;

    #abortController: AbortController | null = null;

    acceptSuggestion(suggestion: NlpSuggestion): void {
        this.nlpState = handleAcceptSuggestion(this.nlpState, suggestion);
    }

    codeChange(result: CodeEditorResult): void {
        this.nlpState = handleCodeChange(this.nlpState, result);
    }

    async confirmParsedResponse(): Promise<void> {
        const parsedStateAtom = this.#nlpState.atom;
        if (TRACE) {
            this.#log.info({
                message: 'Confirming parsed response',
                parsedStateAtom,
                importManger: this.#importsManager.imports.map((i) => i.toContractType()),
            });
        }

        if (parsedStateAtom.status !== 'parsed') {
            if (TRACE) {
                this.#log.info({ message: 'Parsed response is not parsed', parsedStateAtom });
            }
            return;
        }

        if (this.#isConfirming) {
            if (TRACE) {
                this.#log.trace({ message: 'confirmParsedResponse already in flight; ignoring reentrant call' });
            }
            return;
        }

        this.isConfirming = true;
        try {
            const fixedFormula = await this.#importsManager.addImportsFromRefsAndFormula(
                parsedStateAtom.unconfirmedFormula.aliases,
                parsedStateAtom.unconfirmedFormula.formula,
            );

            if (TRACE) {
                this.#log.info({
                    message: 'Fixed formula',
                    fixedFormula,
                    importManger: this.#importsManager.imports.map((i) => i.toContractType()),
                });
            }

            /**
             * Do a basic parse to check if there are any missing imports after confirming.
             *
             * This should be considered a short-term solution, really we need to adjust the NLPEditorService ->
             * CodeEditorService flow to do the same error checking regardless of whether a CodeEditor is being rendered
             * or not.
             */
            await this.documentForConfirmedFormulaValidation.update(
                {
                    analyzers: this.#backtest
                        ? [...this.#importsManager.analyzers, backtestAnalyzer]
                        : this.#importsManager.analyzers,
                    dataTypeRequired: this.#queryType === NlpQueryType.condition ? 'boolean' : undefined,
                },
                fixedFormula,
            );

            if (TRACE) {
                this.#log.trace({ message: 'awaited documentForConfirmedFormulaValidation.updateCode', fixedFormula });
            }

            let errors;
            if (this.documentForConfirmedFormulaValidation.errors.length > 0) {
                errors = this.documentForConfirmedFormulaValidation.errors;
                this.#log.error({ message: 'Errors found during NLP formula confirmation', errors });
            }

            if (TRACE) {
                this.#log.trace({ message: 'handleConfirmParsedResponse', fixedFormula, errors });
            }

            this.nlpState = handleConfirmParsedResponse(this.#nlpState, fixedFormula, errors);
        } finally {
            this.isConfirming = false;
        }
    }

    constructor(
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('CodeDocumentModel') private documentForConfirmedFormulaValidation: ReactBindings['CodeDocumentModel'],
        @inject('Logger') private logger: ReactBindings['Logger'],
        @inject('FeatureFlagService') private featureFlagService: ReactBindings['FeatureFlagService'],
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        this.#log = this.logger.getSubLogger({ name: 'feat:nlp:NLPEditorService' });
    }

    editParsedResponse(): void {
        this.nlpState = handleEditParsedResponse(this.#nlpState);
    }

    editParsedResponseError(): void {
        this.nlpState = handleEditParsedResponseError(this.#nlpState);
    }

    #backtest = false;

    // TODO: Add dataTypeRequired -> NlpQueryType.condition as option
    init({ initialState, importsManager, backtest }: NLPEditorServiceInitOptions): void {
        this.#nlpState = initialState;
        this.#importsManager = importsManager;
        this.#backtest = backtest ?? false;
    }

    get isSubmitting(): boolean {
        return this.#isSubmitting;
    }

    @reacts set isSubmitting(isSubmitting: boolean) {
        this.#isSubmitting = isSubmitting;
    }

    get isConfirming(): boolean {
        return this.#isConfirming;
    }

    @reacts set isConfirming(isConfirming: boolean) {
        this.#isConfirming = isConfirming;
    }

    #log: ReactBindings['Logger'];

    get nlpState(): NaturalLanguageAtomState {
        return this.#nlpState;
    }

    @reacts private set nlpState(nlpState: NaturalLanguageAtomState) {
        this.#nlpState = nlpState;
        this.stateChangeTarget.dispatchEvent(
            new CustomEvent(NLP_STATE_CHANGE_EVENT, { detail: nlpState }) satisfies NLPStateChangeEvent,
        );
    }

    naturalLanguageChange(naturalLanguage: string): void {
        this.nlpState = handleNaturalLanguageChange(this.#nlpState, naturalLanguage);
    }

    rejectParsedResponse(): void {
        this.nlpState = handleRejectParsedResponse(this.#nlpState);
    }

    cancelSubmit(): void {
        if (this.#abortController) {
            this.#abortController.abort();
            this.#abortController = null;
            this.isSubmitting = false;
        }
        if (this.#nlpState.atom.status === 'parsing') {
            this.nlpState = handleRejectParsedResponse(this.#nlpState);
        }
    }

    setMode(mode: NaturalLanguageDisplayMode): void {
        this.nlpState = handleSetMode(this.#nlpState, mode);
    }

    setQueryType(queryType: NlpQueryType): void {
        this.#queryType = queryType;
    }

    stateChangeTarget: EventTarget = new EventTarget();

    async submit(): Promise<void> {
        if (this.#abortController) {
            this.#abortController.abort();
        }

        const controller = new AbortController();
        this.#abortController = controller;

        try {
            this.isSubmitting = true;
            this.nlpState = handleParsing(this.#nlpState);
            const useLlm = this.featureFlagService.getValue(enableLlmNlp);
            const response = await this.queryClient.fetchQuery({
                queryKey: ['nlp', this.#nlpState.naturalLanguage, useLlm, this.#backtest],
                queryFn: () =>
                    parseNaturalLanguageP(
                        this.#nlpState.naturalLanguage,
                        this.#queryType,
                        this.#id,
                        controller.signal,
                        useLlm,
                        this.#backtest,
                    ),
            });

            if (controller.signal.aborted) {
                return;
            }

            if ('result' in response) {
                this.nlpState = handleParsedResponse(this.#nlpState, response);
            } else {
                this.nlpState = handleParsedResponseError(this.#nlpState, response);
            }
        } catch (e: unknown) {
            if (controller.signal.aborted) {
                return;
            }
            throw e;
        } finally {
            if (this.#abortController === controller) {
                this.#abortController = null;
                this.isSubmitting = false;
            }
        }
    }
}
